/**
 * Offline writes — localStorage persistence for the static (API-less) demo.
 *
 * When syriaphr.pages.dev is served without the FastAPI backend, writes that
 * would normally hit the API are stored locally so the demo stays usable in a
 * single browser profile. Storage access is defensive: in private mode, or
 * when storage is unavailable/stubbed (jsdom), every function degrades to a
 * no-op and readers return empty structures.
 *
 * Keys are versioned (`v1`) so a future contract change can bump and ignore
 * stale shapes instead of throwing at runtime.
 */

const NS = 'syriaphr.v1'

export type OfflineRegistration = {
  patient: {
    id: string
    person_id: string
    facility_id: string
    mrn: string
    verification_tier: number
    status: string
    person: {
      id: string
      nhi: string
      name_ar?: string | null
      name_latin?: string | null
      dob?: string | null
      sex?: string | null
      phone?: string | null
    }
  }
  action: 'created_new' | 'linked_existing'
  /* ISO timestamp, informational only */
  created_at: string
}

function safeLocal(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    const store = window.localStorage
    if (!store || typeof store.getItem !== 'function') return null
    // Probe — private mode throws on setItem, not necessarily on getItem.
    const probe = `${NS}.probe`
    store.setItem(probe, '1')
    store.removeItem(probe)
    return store
  } catch {
    return null
  }
}

function read<T>(key: string, fallback: T): T {
  const store = safeLocal()
  if (!store) return fallback
  try {
    const raw = store.getItem(key)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as T
    return parsed ?? fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): boolean {
  const store = safeLocal()
  if (!store) return false
  try {
    store.setItem(key, JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

/* ============================ Registrations ============================ */

const REGISTRATIONS_KEY = `${NS}.offline-registrations`

export function readOfflineRegistrations(): OfflineRegistration[] {
  const rows = read<OfflineRegistration[]>(REGISTRATIONS_KEY, [])
  if (!Array.isArray(rows)) return []
  // Drop anything that doesn't satisfy the minimal registration shape.
  return rows.filter(
    (row): row is OfflineRegistration =>
      !!row &&
      typeof row === 'object' &&
      typeof (row as { patient?: { id?: unknown } }).patient?.id === 'string',
  )
}

export function appendOfflineRegistration(entry: OfflineRegistration): void {
  const rows = readOfflineRegistrations()
  rows.push(entry)
  write(REGISTRATIONS_KEY, rows)
}

/* ============================= Field edits ============================= */

const EDITS_PREFIX = `${NS}.patient-edits.`

export type PatientFieldEdits = {
  name_ar?: string
  name_latin?: string
  dob?: string
  sex?: 'M' | 'F'
  phone?: string
}

export function readPatientEdits(patientId: string): PatientFieldEdits {
  return read<PatientFieldEdits>(EDITS_PREFIX + patientId, {})
}

export function writePatientEdits(patientId: string, edits: PatientFieldEdits): void {
  write(EDITS_PREFIX + patientId, edits)
}

/* ==================== Medications / family history ==================== */

const MEDS_PREFIX = `${NS}.meds.`
const FH_PREFIX = `${NS}.fh.`

export type OfflineMedication = {
  id: string
  patient_id: string
  reported_by: 'patient' | 'clinician'
  description: string
  rxnorm_code: string | null
  status: string
  date_recorded: string
  source: string | null
  note: string | null
  dose: string | null
  dose_unit: string | null
  route: string | null
  form: string | null
  frequency_code: string | null
  duration: string | null
  quantity: string | null
  refills: number | null
  indication: string | null
  prescriber_id: string | null
  pharmacy_formulary_notes: string | null
  dispense_instructions: string | null
  confidence_tier: number
  created_at?: string | null
  updated_at?: string | null
}

export function readOfflineMeds(patientId: string): OfflineMedication[] {
  const rows = read<OfflineMedication[]>(MEDS_PREFIX + patientId, [])
  return Array.isArray(rows) ? rows : []
}

export function writeOfflineMeds(patientId: string, items: OfflineMedication[]): void {
  write(MEDS_PREFIX + patientId, items)
}

export type OfflineFamilyHistoryCondition = {
  id: string
  family_history_relative_id: string
  snomed_code: string
  name: string
  age_at_onset: number | null
  last_confirmed_at: string | null
  needs_reconfirmation: boolean
  confirmed_until: string | null
}

export type OfflineFamilyHistoryRelative = {
  id: string
  patient_id: string
  relationship: string
  relationship_code: string | null
  relationship_label: string | null
  relative_name: string | null
  relative_birth_year: number | null
  deceased: boolean
  age_at_death: number | null
  certainty: string
  record_status: string
  notes: string | null
  conditions: OfflineFamilyHistoryCondition[]
  needs_reconfirmation: boolean
}

export function readOfflineFamilyHistory(patientId: string): OfflineFamilyHistoryRelative[] {
  const rows = read<OfflineFamilyHistoryRelative[]>(FH_PREFIX + patientId, [])
  return Array.isArray(rows) ? rows : []
}

export function writeOfflineFamilyHistory(
  patientId: string,
  items: OfflineFamilyHistoryRelative[],
): void {
  write(FH_PREFIX + patientId, items)
}

/* ======================= API-down detection ======================= */

/**
 * Demo-mode trigger detection (static Cloudflare Pages with no API).
 *
 * Returns true for failures that mean the backend is unreachable/not deployed:
 *   - TypeError: raw fetch network failure or CORS wall.
 *   - ApiError 404/405/501: static host has no route (Pages Functions absent).
 *
 * Returns false for genuine live-API rejections (422 validation, 409
 * conflict, 5xx from a real backend) so server errors stay honest.
 */
export function isApiLikelyDown(err: unknown): boolean {
  if (err instanceof TypeError) return true
  if (err instanceof Error && err.name === 'ApiError') {
    const status = (err as { status?: number }).status
    return status === 404 || status === 405 || status === 501
  }
  return false
}

/* ============================ ID generation ============================ */

/**
 * Local NHI generation for offline registration — mirrors the backend
 * contract `SYR-<year>-<5-digit sequence><Luhn check digit>` so demo numbers
 * look exactly like live-issued ones (see apps/api/app/services/nhi.py).
 */

function luhnCheckDigit(digits: string): string {
  let sum = 0
  // Right-to-left Luhn over the base digits; the check digit occupies the
  // final position of the 6-digit sequence.
  for (let i = digits.length - 1, double = false; i >= 0; i--, double = !double) {
    let value = digits.charCodeAt(i) - 48
    if (double) {
      value *= 2
      if (value > 9) value -= 9
    }
    sum += value
  }
  return String((10 - (sum % 10)) % 10)
}

export function generateLocalNhi(sequence?: number): string {
  const year = new Date().getFullYear()
  // Random 5-digit sequence; deterministic when a sequence is provided.
  const body = String(sequence ?? Math.floor(10000 + Math.random() * 90000)).padStart(5, '0')
  return `SYR-${year}-${body}${luhnCheckDigit(body)}`
}